import { createEntityAdapter, createSelector, createSlice, EntityState, isAnyOf, PayloadAction } from "@reduxjs/toolkit";
import { WritableDraft } from "immer/dist/internal";
import { AppId } from "../../app/appTypes";
import { RootState } from "../../app/store";
import { createDocSection, deleteDocSection, DocumentBlock, DocumentContentActions, DocumentHeader, DocumentSection, DocumentWorkflowActions, generateDocBlock, loadDocBlock, loadDocument, loadDocumentActions, loadDocumentSections, moveDocSection, performDocumentAction, updateDocBlock, updateDocSection, updateDocumentSetting } from "./documentsApi";
import { createFormSection } from "./sections/formSection/formSectionApi";
import { changeDocCommentStatus, createComment, deleteDocComment, DocComment, loadDocComments, updateComment } from "./commentsAPI";
import { createDocumentTest, createTestRun, deleteTestRun, DocumentTest, linkTestRelatedObject, loadDocumentAllTests, loadDocumentSectionTests, signTestRun, softDeleteDocumentTest, TestCriteriaResult, TestRun, unlinkTestRelatedObject, updateDocumentTest, updateTestRun } from "./documentTests/documentTestsApi";
import { loadDocumentValidatedObject, ValidatedObject } from "../project/validationProcess/validatedObjectApi";
import { analyseDeviation, closeDeviation, correctionDeviation, createDeviation, Deviation, evaluateDeviation, loadDeviations, updateDeviation } from "./sections/deviationsSection/deviationsApi";
import { createFunction, deleteFunction, loadRiskAnalysisContent, reorderRiskAnalysisFunctions, RiskAnalysisConsequence, RiskAnalysisContent, RiskAnalysisDefect, RiskAnalysisFunction, RiskAnalysisGroup, RiskAnalysisReason, updateFunction } from "./riskAnalysis/riskAnalysisApi";
import { loadDocumentProcess } from "../project/projectProcess/ProjectProcessApiClient";
import { ProcessStageOverview } from "../project/validationProcess/validationProcessApi";

const sectionAdapter = createEntityAdapter<DocumentSection>({
    selectId: section => section.id,
    sortComparer: (a, b) => a.order - b.order,
})

const blockAdapter = createEntityAdapter<DocumentBlock>({
    selectId: content => content.sectionId,
})

const docWfActionsAdapter = createEntityAdapter<DocumentWorkflowActions>({
    selectId: x => x
})

const docActionsAdapter = createEntityAdapter<DocumentContentActions>({
    selectId: x => x
})

const commentAdapter = createEntityAdapter<DocComment>({
    selectId: c => c.id,
})

const testAdapter = createEntityAdapter<DocumentTest>({
    selectId: t => t.id,
    sortComparer: (a, b) => a.order - b.order,
})

const testRunAdapter = createEntityAdapter<TestRun>({
    selectId: t => t.id,
})

const testResultAdapter = createEntityAdapter<TestCriteriaResult>({
    selectId: t => t.id
})

const deviationAdapter = createEntityAdapter<Deviation>({
    selectId: dev => dev.id
})

const fnAdapter = createEntityAdapter<RiskAnalysisFunction>({
    selectId: fn => fn.id,
    sortComparer: (a, b) => a.order - b.order,
})

interface TestState {
    tests: EntityState<DocumentTest>
    runs: EntityState<TestRun>
    results: EntityState<TestCriteriaResult>
    loaded: boolean
}

export interface DocumentProcess {
    processId: string
    stages: ProcessStageOverview[]
}

export interface DocumentState {
    header: DocumentHeader
    sections: EntityState<DocumentSection> | null
    blocks: EntityState<DocumentBlock> | null
    status: string | null
    workflowActions: EntityState<DocumentWorkflowActions> | null
    actions: EntityState<DocumentContentActions> | null
    comments: EntityState<DocComment> | null
    tests: TestState
    validatedObject: ValidatedObject | null
    deviations: { loaded: boolean, items:  EntityState<Deviation> }
    functions: { loaded: boolean, items: EntityState<RiskAnalysisFunction> }
    // Client-side index: RA group (section) id -> ordered function ids.
    // The RA groups are document sections and already live in `sections`, but the
    // API returns functions only nested inside the group tree — RiskAnalysisFunction
    // itself carries no parent reference — so the link is captured here on load.
    raGroupFunctions: Record<AppId, number[]>
    // Retained copy of the synthesized RA group sections. GET /sections never
    // returns them, so any wholesale section refresh (setAll) would drop them;
    // this lets us put them back.
    raGroupSections: DocumentSection[]
    activeProcess?: DocumentProcess
}

// Flatten the RA content group tree into `group id -> function ids`, preserving
// the order the API returned them in.
function indexGroupFunctions(groups: RiskAnalysisGroup[]): Record<AppId, number[]> {
    const index: Record<AppId, number[]> = {}
    const visit = (group: RiskAnalysisGroup) => {
        index[group.id] = group.functions.map(fn => fn.id)
        group.groups.forEach(visit)
    }
    groups.forEach(visit)
    return index
}

// GET /risk-analysis/{id}/content returns the RISK_ANALYSIS section itself, so
// `functions` holds only the functions hanging off that section — every other
// function is nested inside a group. Collect them all for the entity store.
function collectAllFunctions(content: RiskAnalysisContent): RiskAnalysisFunction[] {
    const all = [...content.functions]
    const visit = (group: RiskAnalysisGroup) => {
        all.push(...group.functions)
        group.groups.forEach(visit)
    }
    content.groups.forEach(visit)
    return all
}

/**
 * Absorbs an RA content payload (the RISK_ANALYSIS section with its group tree).
 * Both GET /content and POST /content/functions/reorder return this same shape,
 * so both go through here — a reorder response carries the full tree, and taking
 * only its top-level `functions` would drop every grouped one.
 */
function ingestRiskAnalysisContent(
    doc: WritableDraft<DocumentState> | null | undefined,
    content: RiskAnalysisContent,
) {
    if (!doc) {
        return
    }
    doc.functions.loaded = true
    fnAdapter.setAll(doc.functions.items, collectAllFunctions(content))

    const raSection = Object.values(doc.sections?.entities ?? {})
        .find(s => s?.type === 'RISK_ANALYSIS')

    const index = indexGroupFunctions(content.groups)
    if (raSection) {
        // Functions attached directly to the RA section, i.e. ungrouped.
        index[raSection.id] = content.functions.map(fn => fn.id)

        const groupSections = mapGroupsToSections(
            content.groups,
            Number(doc.header.id),
            raSection.id,
        )
        doc.raGroupSections = groupSections
        if (doc.sections) {
            sectionAdapter.upsertMany(doc.sections, groupSections)
        }
    }
    doc.raGroupFunctions = index
}

// RA groups ARE document sections, but GET /documents/{id}/sections never returns
// them: DocumentSectionService.AddWithSubsections stops descending once it reaches
// a RISK_ANALYSIS section. The RA content response is therefore the only place
// they arrive, so map them into DocumentSection entities here.
function mapGroupsToSections(
    groups: RiskAnalysisGroup[],
    documentId: number,
    raSectionId: AppId,
): DocumentSection[] {
    const sections: DocumentSection[] = []
    const visit = (group: RiskAnalysisGroup, parentSectionGuid: AppId) => {
        sections.push({
            id: group.id,
            documentId,
            code: group.code,
            name: group.name,
            description: '',
            numeration: group.numeration,
            order: group.order ?? 0,
            sysSectionId: '',
            type: group.type ?? 'GROUP',
            modifiedByFullName: '',
            modifyDate: '',
            parentSectionGuid,
            subSectionsIds: group.groups.map(child => child.id),
        })
        group.groups.forEach(child => visit(child, group.id))
    }
    groups.forEach(group => visit(group, raSectionId))
    return sections
}

const initialState: { doc: DocumentState | null } = { doc: null }

// The RA group sections are not part of any /sections response, so re-apply the
// retained copy after a wholesale refresh or they silently disappear.
function reapplyRaGroupSections(doc: WritableDraft<DocumentState> | null | undefined) {
    if (doc?.sections && doc.raGroupSections?.length) {
        // `sections` may have just been reassigned, in which case immer hands back
        // a plain object and the adapter returns new state rather than mutating —
        // so take the result rather than relying on in-place mutation.
        doc.sections = sectionAdapter.upsertMany(
            doc.sections,
            doc.raGroupSections.map(s => ({ ...s })) as DocumentSection[],
        )
    }
}

function setSections(state: WritableDraft<{ doc: DocumentState | null; }>, action: PayloadAction<DocumentSection[]>) {
    if (state.doc) {
        state.doc.sections = sectionAdapter.getInitialState()
        state.doc.sections = sectionAdapter.setAll(state.doc.sections, action.payload)
        reapplyRaGroupSections(state.doc)
    }
}

function addCreatedSection(state: WritableDraft<{ doc: DocumentState | null; }>, s: DocumentSection) {
    if (state.doc && state.doc.sections) {
        if (s.parentSectionGuid) {
            state.doc.sections.entities[s.parentSectionGuid]?.subSectionsIds.push(s.id)
        }
        sectionAdapter.addOne(state.doc.sections, s)
    }
}

function setDocumentHeader(state: WritableDraft<{ doc: DocumentState | null; }>, action: PayloadAction<DocumentHeader>) {
    state.doc = {
        header: action.payload,
        sections: sectionAdapter.getInitialState(),
        blocks: blockAdapter.getInitialState(),
        status: null,
        workflowActions: docWfActionsAdapter.getInitialState(),
        actions: docActionsAdapter.getInitialState(),
        comments: commentAdapter.getInitialState(),
        tests: {
            tests: testAdapter.getInitialState(),
            runs: testRunAdapter.getInitialState(),
            results: testResultAdapter.getInitialState(),
            loaded: false,
        },
        validatedObject: null,
        deviations: { loaded: false, items:  deviationAdapter.getInitialState() },
        functions: { loaded: false, items:  fnAdapter.getInitialState() },
        raGroupFunctions: {},
        raGroupSections: [],
    }
}

function setDocStatus(state: WritableDraft<{ doc: DocumentState | null; }>, action: PayloadAction<string>) {
    if (state.doc) {
        state.doc.status = action.payload
    }
}

function setWfActions(state: WritableDraft<{ doc: DocumentState | null; }>, action: PayloadAction<DocumentWorkflowActions[]>) {
    if (state.doc) {
        if (state.doc.workflowActions === null) {
            state.doc.workflowActions = docWfActionsAdapter.getInitialState()
        }
        state.doc.workflowActions = docWfActionsAdapter.setAll(state.doc.workflowActions, action.payload)
    }
}

function setActions(state: WritableDraft<{ doc: DocumentState | null; }>, action: PayloadAction<DocumentContentActions[]>) {
    if (state.doc) {
        if (state.doc.actions === null) {
            state.doc.actions = docActionsAdapter.getInitialState()
        }
        state.doc.actions = docActionsAdapter.setAll(state.doc.actions, action.payload)
    }
}

function setComments(state, action: PayloadAction<DocComment[]>) {
    if (state.doc) {
        state.doc.comments = commentAdapter.getInitialState()
        state.doc.comments = commentAdapter.setAll(state.doc.comments, action.payload)
    }
}

export const documentSlice = createSlice({
    name: "document",
    initialState,
    reducers: {
        setDocumentHeader,
        updateDocumentHeader: (state, action: PayloadAction<DocumentHeader>) => {
            if (state.doc) {
                if (state.doc.header.id === action.payload.id) {
                    state.doc.header = action.payload
                }
            }
        },
        setSections,
        setSection: (state, action: PayloadAction<DocumentSection>) => {
            if (state.doc) {
                if (state.doc.sections === null) {
                    state.doc.sections = sectionAdapter.getInitialState()
                }
                state.doc.sections = sectionAdapter.addOne(state.doc.sections, action.payload)
            }
        },
        updateSection: (state, action: PayloadAction<DocumentSection>) => {
            const { id } = action.payload
            const changes: Omit<DocumentSection, "id"> = action.payload
            if (state.doc && state.doc.sections) {
                state.doc.sections = sectionAdapter.updateOne(state.doc.sections, {
                    id,
                    changes,
                })
            }
        },
        swapSections: (state, action: PayloadAction<{ s1: AppId, s2: AppId }>) => {
            const { s1, s2 } = action.payload
            if (state.doc && state.doc.sections) {
                const section1order = state.doc.sections.entities[s1]?.order
                const section2order = state.doc.sections.entities[s2]?.order
                if (section1order && section2order) {
                    state.doc.sections = sectionAdapter.updateMany(state.doc.sections, [
                        { id: s1, changes: { order: section2order } },
                        { id: s2, changes: { order: section1order } },
                    ])
                }
            }
        },
        setBlock: (state, action: PayloadAction<DocumentBlock>) => {
            if (state.doc) {
                if (state.doc.blocks === null) {
                    state.doc.blocks = blockAdapter.getInitialState()
                }
                state.doc.blocks = blockAdapter.addOne(state.doc.blocks, action.payload)
            }
        },
        updateBlock: (state, action: PayloadAction<DocumentBlock>) => {
            const { sectionId } = action.payload
            const changes: Omit<DocumentBlock, "sectionId"> = action.payload
            if (state.doc && state.doc.blocks) {
                state.doc.blocks = blockAdapter.updateOne(state.doc.blocks, {
                    id: sectionId,
                    changes,
                })
            }
        },
        changeTestRun: (state, action: PayloadAction<{ id: number, changes: Partial<TestRun> }>) => {
            const { id, changes } = action.payload
            if (state.doc) {
                testRunAdapter.updateOne(state.doc.tests.runs, { id, changes })
            }
        },
        changeTestResult: (state, action: PayloadAction<{ id: number, changes: Partial<TestCriteriaResult> }>) => {
            const { id, changes } = action.payload
            if (state.doc) {
                testResultAdapter.updateOne(state.doc.tests.results, { id, changes })
            }
        },
        setFunctions: (state, action: PayloadAction<RiskAnalysisFunction[]>) => {
            if (state.doc && state.doc.functions.loaded) {
                fnAdapter.setAll(state.doc.functions.items, action.payload)
            }
        },
        pushDefect: (state, action: PayloadAction<RiskAnalysisDefect>) => {
            if (state.doc && state.doc.functions.loaded) {
                const fn = state.doc.functions.items.entities[action.payload.functionId]
                if (fn) {
                    fn.defects.push(action.payload)
                }
            }
        },
        setDefect: (state, action: PayloadAction<RiskAnalysisDefect>) => {
            const { id, ...data } = action.payload
            if (state.doc && state.doc.functions.loaded) {
                const fn = state.doc.functions.items.entities[action.payload.functionId]
                if (fn) {
                    const i = fn.defects.findIndex(d => d.id === id)
                    if (i >= 0) {
                        const d = fn.defects[i]
                        d.name = data.name
                        d.value = data.value
                    }
                }
            }
        },
        delDefect: (state, action: PayloadAction<{
            fnId: number,
            defectId: number,
        }>) => {
            const { fnId, defectId } = action.payload
            if (state.doc && state.doc.functions.loaded) {
                const fn = state.doc.functions.items.entities[fnId]
                if (fn) {
                    const defectIdx = fn.defects.findIndex(d => d.id === defectId)
                    if (defectIdx >= 0) {
                        fn.defects.splice(defectIdx, 1)
                    }
                }
            }
        },
        pushConsequence: (state, action: PayloadAction<{
            fnId: number,
            defectId: number,
            consequence: RiskAnalysisConsequence
        }>) => {
            const { fnId, defectId, consequence } = action.payload
            if (state.doc && state.doc.functions.loaded) {
                const fn = state.doc.functions.items.entities[fnId]
                if (fn) {
                    const defectIdx = fn.defects.findIndex(d => d.id === defectId)
                    if (defectIdx >= 0) {
                        const defect = fn.defects[defectIdx]
                        defect.consequences.push(consequence)
                    }
                }
            }
        },
        setConsequence: (state, action: PayloadAction<{
            fnId: number,
            defectId: number,
            consequence: RiskAnalysisConsequence
        }>) => {
            const { fnId, defectId, consequence } = action.payload
            if (state.doc && state.doc.functions.loaded) {
                const fn = state.doc.functions.items.entities[fnId]
                if (fn) {
                    const defectIdx = fn.defects.findIndex(d => d.id === defectId)
                    if (defectIdx >= 0) {
                        const defect = fn.defects[defectIdx]
                        const consequenceIdx = defect.consequences.findIndex(c => c.id === consequence.id)
                        if (consequenceIdx >= 0) {
                            const c = defect.consequences[consequenceIdx]
                            const { name, severity } = consequence
                            c.name = name
                            c.severity = severity
                        }
                    }
                }
            }
        },
        delConsequence: (state, action: PayloadAction<{
            fnId: number,
            defectId: number,
            consequenceId: number,
        }>) => {
            const { fnId, defectId, consequenceId } = action.payload
            if (state.doc && state.doc.functions.loaded) {
                const fn = state.doc.functions.items.entities[fnId]
                if (fn) {
                    const defectIdx = fn.defects.findIndex(d => d.id === defectId)
                    if (defectIdx >= 0) {
                        const defect = fn.defects[defectIdx]
                        const consequenceIdx = defect.consequences.findIndex(c => c.id === consequenceId)
                        if (consequenceIdx >= 0) {
                            defect.consequences.splice(consequenceIdx, 1)
                        }
                    }
                }
            }
        },
        pushReason: (state, action: PayloadAction<{
            fnId: number,
            defectId: number,
            consequenceId: number,
            reason: RiskAnalysisReason
        }>) => {
            const { fnId, defectId, consequenceId, reason } = action.payload
            if (state.doc && state.doc.functions.loaded) {
                const fn = state.doc.functions.items.entities[fnId]
                if (fn) {
                    const defectIdx = fn.defects.findIndex(d => d.id === defectId)
                    if (defectIdx >= 0) {
                        const defect = fn.defects[defectIdx]
                        const consequenceIdx = defect.consequences.findIndex(c => c.id === consequenceId)
                        if (consequenceIdx >= 0) {
                            defect.consequences[consequenceIdx].reasons.push(reason)
                        }
                    }
                }
            }
        },
        setReason: (state, action: PayloadAction<{
            fnId: number,
            defectId: number,
            consequenceId: number,
            reason: RiskAnalysisReason
        }>) => {
            const { fnId, defectId, consequenceId, reason } = action.payload
            if (state.doc && state.doc.functions.loaded) {
                const fn = state.doc.functions.items.entities[fnId]
                if (fn) {
                    const defectIdx = fn.defects.findIndex(d => d.id === defectId)
                    if (defectIdx >= 0) {
                        const defect = fn.defects[defectIdx]
                        const consequenceIdx = defect.consequences.findIndex(c => c.id === consequenceId)
                        if (consequenceIdx >= 0) {
                            const cons = defect.consequences[consequenceIdx]
                            const dIdx = cons.reasons.findIndex(r => r.id === reason.id)
                            if (dIdx >= 0) {
                                const { name, currentControlMethod, occurrenceProbability, detectionProbability } = reason
                                const r = cons.reasons[dIdx]
                                r.name = name
                                r.currentControlMethod = currentControlMethod
                                r.occurrenceProbability = occurrenceProbability
                                r.detectionProbability = detectionProbability
                                r.recommendedAction = reason.recommendedAction
                                r.hasDQ = reason.hasDQ
                                r.hasIQ = reason.hasIQ
                                r.hasOQ = reason.hasOQ
                                r.hasPQ = reason.hasPQ
                            }
                        }
                    }
                }
            }
        },
        delReason: (state, action: PayloadAction<{
            fnId: number,
            defectId: number,
            consequenceId: number,
            reasonId: number
        }>) => {
            const { fnId, defectId, consequenceId, reasonId } = action.payload
            if (state.doc && state.doc.functions.loaded) {
                const fn = state.doc.functions.items.entities[fnId]
                if (fn) {
                    const defectIdx = fn.defects.findIndex(d => d.id === defectId)
                    if (defectIdx >= 0) {
                        const defect = fn.defects[defectIdx]
                        const consequenceIdx = defect.consequences.findIndex(c => c.id === consequenceId)
                        if (consequenceIdx >= 0) {
                            const consequence = defect.consequences[consequenceIdx]
                            const i = consequence.reasons.findIndex(r => r.id === reasonId)
                            if (i >= 0) {
                                consequence.reasons.splice(i, 1)
                            }
                        }
                    }
                }
            }
        },
        // AI generation deletes everything under the defect/consequence on the
        // server and returns the full replacement, so these swap, not append.
        replaceConsequences: (state, action: PayloadAction<{
            fnId: number,
            defectId: number,
            consequences: RiskAnalysisConsequence[]
        }>) => {
            const { fnId, defectId, consequences } = action.payload
            if (state.doc && state.doc.functions.loaded) {
                const fn = state.doc.functions.items.entities[fnId]
                const defect = fn?.defects.find(d => d.id === defectId)
                if (defect) {
                    defect.consequences = consequences
                }
            }
        },
        replaceReasons: (state, action: PayloadAction<{
            fnId: number,
            defectId: number,
            consequenceId: number,
            reasons: RiskAnalysisReason[]
        }>) => {
            const { fnId, defectId, consequenceId, reasons } = action.payload
            if (state.doc && state.doc.functions.loaded) {
                const fn = state.doc.functions.items.entities[fnId]
                const consequence = fn?.defects.find(d => d.id === defectId)
                    ?.consequences.find(c => c.id === consequenceId)
                if (consequence) {
                    consequence.reasons = reasons
                }
            }
        },
        setDocStatus,
        setWfActions,
        setActions,
        setComments,
    },
    extraReducers: builder => {
        builder.addCase(loadDocumentSections.fulfilled, (state, action) => {
            setSections(state, action)
        })
        builder.addCase(loadDocument.fulfilled, (state, action) => {
            setDocumentHeader(state, action)
        })
        builder.addCase(loadDocumentActions.fulfilled, (state, action) => {
            const { documentStatus, workflowActions, documentContentActions } = action.payload
            setDocStatus(state, { ...action, payload: documentStatus })
            setWfActions(state, { ...action, payload: workflowActions })
            setActions(state, { ...action, payload: documentContentActions })
        })
        builder.addCase(loadDocComments.fulfilled, (state, action) => {
            setComments(state, action)
        })
        builder.addCase(createDocSection.fulfilled, (state, action) => {
            addCreatedSection(state, action.payload)
        })
        builder.addCase(createFormSection.fulfilled, (state, action) => {
            addCreatedSection(state, action.payload.section)
        })
        builder.addCase(updateDocSection.fulfilled, (state, action) => {
            if (state.doc && state.doc.sections) {
                const { id, ...rest } = action.payload
                const updt = {
                    id,
                    changes: rest,
                }
                sectionAdapter.updateOne(state.doc.sections, updt)
            }
        })
        builder.addCase(deleteDocSection.fulfilled, (state, action) => {
            const { sections } = action.payload
            if (state.doc && state.doc.sections) {
                sectionAdapter.setAll(state.doc.sections, sections)
                reapplyRaGroupSections(state.doc)
            }
        })
        builder.addCase(moveDocSection.fulfilled, (state, action) => {
            if (state.doc && state.doc.sections) {
                const sections = action.payload
                const updt = sections.map(({ id, order, numeration }) => {
                    return {
                        id,
                        changes: {
                            order,
                            numeration,
                        },
                    }
                })
                sectionAdapter.updateMany(state.doc.sections, updt)
            }
        })
        builder.addCase(loadDocBlock.fulfilled, (state, action) => {
            if (state.doc) {
                if (state.doc.blocks === null) {
                    state.doc.blocks = blockAdapter.getInitialState()
                }
                blockAdapter.setOne(state.doc.blocks, action.payload)
            }
        })
        builder.addCase(updateDocBlock.fulfilled, (state, action) => {
            if (state.doc && state.doc.blocks) {
                const { sectionId, description } = action.payload
                blockAdapter.updateOne(state.doc.blocks, {
                    id: sectionId,
                    changes: {
                        description,
                    }
                })
            }
            const { positions } = action.meta.arg
            if (state.doc && state.doc.comments && positions && positions.length > 0) {
                commentAdapter.updateMany(state.doc.comments, positions.map(p => ({
                    id: p.commentId,
                    changes: { pos: p.pos },
                })))
            }
        })
        builder.addCase(generateDocBlock.fulfilled, (state, action) => {
            if (state.doc && state.doc.blocks) {
                const { sectionId, description } = action.payload
                blockAdapter.updateOne(state.doc.blocks, {
                    id: sectionId,
                    changes: {
                        description,
                    }
                })
            }
        })
        builder.addCase(createComment.fulfilled, (state, action) => {
            const c = action.payload
            if (state.doc) {
                if (state.doc.comments === null) {
                    state.doc.comments = commentAdapter.getInitialState()
                }
                if (c.parentCommentId !== null) {
                    const rs = state.doc.comments.entities[c.parentCommentId]?.replies ?? []
                    commentAdapter.updateOne(state.doc.comments, {
                        id: c.parentCommentId,
                        changes: {
                            replies: [...rs, c],
                        },
                    })
                } else {
                    commentAdapter.setOne(state.doc.comments, c)
                }
            }
        })
        builder.addCase(deleteDocComment.fulfilled, (state, action) => {
            if (state.doc && state.doc.comments) {
                commentAdapter.removeOne(state.doc.comments, action.payload)
                // Replies are not entities of their own — createComment.fulfilled
                // pushes them into the parent's `replies` array — so removeOne
                // above is a no-op for one, and a deleted reply would sit in the
                // store until the next reload. Any component reading replies from
                // the store rather than from a local copy sees it.
                for (const id of state.doc.comments.ids) {
                    const parent = state.doc.comments.entities[id]
                    if (parent && parent.replies.some(r => r.id === action.payload)) {
                        parent.replies = parent.replies.filter(r => r.id !== action.payload)
                    }
                }
            }
        })
        builder.addCase(performDocumentAction.fulfilled, (state, action) => {
            if (state.doc) {
                state.doc.header = action.payload.documentHeader

                if (state.doc.workflowActions === null) {
                    state.doc.workflowActions = docWfActionsAdapter.getInitialState()
                }
                state.doc.workflowActions = docWfActionsAdapter.setAll(state.doc.workflowActions, action.payload.workflowActions)

                if (state.doc.actions === null) {
                    state.doc.actions = docActionsAdapter.getInitialState()
                }
                state.doc.actions = docActionsAdapter.setAll(state.doc.actions, action.payload.documentContentActions)
            }
        })
        builder.addCase(updateDocumentSetting.fulfilled, (state, action) => {
            if (state.doc) {
                const { name } = action.payload
                const idx = state.doc.header.settings.findIndex(s => s.name === name)
                if (idx >= 0) {
                    state.doc.header.settings[idx] = action.payload
                } else {
                    state.doc.header.settings.push(action.payload)
                }
            }
        })
        builder.addCase(loadDocumentSectionTests.fulfilled, (state, action) => {
            if (state.doc) {
                testAdapter.setMany(state.doc.tests.tests, action.payload)
                for (let i = 0; i < action.payload.length; i++) {
                    const { runs } = action.payload[i];
                    testRunAdapter.addMany(state.doc.tests.runs, runs)
                    for (let j = 0; j < runs.length; j++) {
                        const r = runs[j].criteriaResults;
                        testResultAdapter.addMany(state.doc.tests.results, r)
                    }
                }
                state.doc.tests.loaded = true
            }
        })
        builder.addCase(loadDocumentAllTests.fulfilled, (state, action) => {
            if (state.doc) {
                testAdapter.setAll(state.doc.tests.tests, action.payload)
                for (let i = 0; i < action.payload.length; i++) {
                    const { runs } = action.payload[i];
                    testRunAdapter.addMany(state.doc.tests.runs, runs)
                    for (let j = 0; j < runs.length; j++) {
                        const r = runs[j].criteriaResults;
                        testResultAdapter.addMany(state.doc.tests.results, r)
                    }
                }
                state.doc.tests.loaded = true
            }
        })
        builder.addCase(createDocumentTest.fulfilled, (state, action) => {
            if (state.doc && state.doc.tests.loaded) {
                testAdapter.addOne(state.doc.tests.tests, action.payload)
            }
        })
        builder.addCase(updateDocumentTest.fulfilled, (state, action) => {
            if (state.doc && state.doc.tests.loaded) {
                const { id: testId, ...data } = action.payload
                testAdapter.updateOne(state.doc.tests.tests, {
                    id: testId,
                    changes: data,
                })
            }
        })
        // Both endpoints return the whole test, so the link list arrives with it and
        // the function it was dropped on picks it up on the next render.
        builder.addCase(linkTestRelatedObject.fulfilled, (state, action) => {
            if (state.doc && state.doc.tests.loaded) {
                const { id, ...data } = action.payload
                testAdapter.updateOne(state.doc.tests.tests, { id, changes: data })
            }
        })
        // Mirrors the removal locally, since the DELETE helper discards the response
        // body. Only the link list changes — the test itself and its runs survive.
        builder.addCase(unlinkTestRelatedObject.fulfilled, (state, action) => {
            if (state.doc && state.doc.tests.loaded) {
                const { testId, relatedObjectId } = action.payload
                const test = state.doc.tests.tests.entities[testId]
                if (test) {
                    testAdapter.updateOne(state.doc.tests.tests, {
                        id: testId,
                        changes: {
                            relatedObjectIds: (test.relatedObjectIds ?? [])
                                .filter(id => id !== relatedObjectId),
                        },
                    })
                }
            }
        })
        builder.addCase(softDeleteDocumentTest.fulfilled, (state, action) => {
            if (state.doc) {
                testAdapter.removeOne(state.doc.tests.tests, action.meta.arg.testId)
            }
        })
        builder.addCase(createTestRun.fulfilled, (state, action) => {
            if (state.doc && state.doc.tests.loaded) {
                const { id, ...testData } = action.payload
                testAdapter.updateOne(state.doc.tests.tests, { id, changes: testData })
                testRunAdapter.upsertMany(state.doc.tests.runs, action.payload.runs)
                let results: TestCriteriaResult[] = []
                for (let i = 0; i < testData.runs.length; i++) {
                    const run = testData.runs[i];
                    results = results.concat(run.criteriaResults)
                }
                testResultAdapter.upsertMany(state.doc.tests.results, results)
            }
        })
        builder.addCase(updateTestRun.fulfilled, (state, action) => {
            if (state.doc && state.doc.tests.loaded) {
                const { id, ...testData } = action.payload
                testAdapter.updateOne(state.doc.tests.tests, { id, changes: testData })
                testRunAdapter.upsertMany(state.doc.tests.runs, action.payload.runs)
                let results: TestCriteriaResult[] = []
                for (let i = 0; i < testData.runs.length; i++) {
                    const run = testData.runs[i];
                    results = results.concat(run.criteriaResults)
                }
                testResultAdapter.upsertMany(state.doc.tests.results, results)
            }
        })
        builder.addCase(deleteTestRun.fulfilled, (state, action) => {
            if (state.doc && state.doc.tests.loaded) {
                const args = action.meta.arg
                const { id: testId, ...data } = action.payload
                testAdapter.updateOne(state.doc.tests.tests, {
                    id: testId,
                    changes: data,
                })
                testRunAdapter.removeOne(state.doc.tests.runs, args.runId)
                let resultIds: number[] = []
                for (let i = 0; i < state.doc.tests.results.ids.length; i++) {
                    const id = state.doc.tests.results.ids[i];
                    const entity = state.doc.tests.results.entities[id]
                    if (entity && entity.runId === args.runId) {
                        resultIds.push(entity.id)
                    }
                }
                testResultAdapter.removeMany(state.doc.tests.results, resultIds)
            }
        })
        builder.addCase(signTestRun.fulfilled, (state, action) => {
            const sig = action.payload
            const arg = action.meta.arg
            if (state.doc && state.doc.tests.loaded) {
                const run = state.doc.tests.runs.entities[arg.runId]
                if (run) {
                    const p = run.participants.find(p => p.id === arg.participantId)
                    if (p) {
                        p.signatureId = sig.signatureId
                    }
                }
            }
        })
        builder.addCase(loadDocumentValidatedObject.fulfilled, (state, action) => {
            if (state.doc) {
                state.doc.validatedObject = action.payload
            }
        })
        builder.addCase(loadDeviations.fulfilled, (state, action) => {
            if (state.doc) {
                state.doc.deviations.loaded = true
                deviationAdapter.setAll(state.doc.deviations.items, action.payload)
            }
        })
        builder.addCase(createDeviation.fulfilled, (state, action) => {
            if (state.doc && state.doc.deviations) {
                deviationAdapter.addOne(state.doc.deviations.items, action.payload)
            }
        })
        builder.addCase(updateDeviation.fulfilled, (state, action) => {
            if (state.doc && state.doc.deviations !== null) {
                const { id, ...changes } = action.payload
                deviationAdapter.updateOne(state.doc.deviations.items, {
                    id, changes
                })
            }
        })
        builder.addCase(loadRiskAnalysisContent.fulfilled, (state, action) => {
            ingestRiskAnalysisContent(state.doc, action.payload)
        })
        builder.addCase(createFunction.fulfilled, (state, action) => {
            const doc = state.doc
            if (doc && doc.functions.loaded) {
                fnAdapter.addOne(doc.functions.items, action.payload)

                // The response carries no parent reference, so take the owning
                // group from the request; a null parent means the RA section itself.
                const raSectionId = Object.values(doc.sections?.entities ?? {})
                    .find(s => s?.type === 'RISK_ANALYSIS')?.id
                const parentId = action.meta.arg.parentSectionGuid ?? raSectionId
                if (parentId) {
                    const current = doc.raGroupFunctions[parentId] ?? []
                    if (!current.includes(action.payload.id)) {
                        doc.raGroupFunctions[parentId] = [...current, action.payload.id]
                    }
                }
            }
        })
        builder.addCase(updateFunction.fulfilled, (state, action) => {
            if (state.doc && state.doc.functions.loaded) {
                const { id, defects, ...data } = action.payload
                fnAdapter.updateOne(state.doc.functions.items, {
                    id,
                    changes: data,
                })
            }
        })
        builder.addCase(reorderRiskAnalysisFunctions.fulfilled, (state, action) => {
            // Same payload shape as a content load, so absorb it the same way.
            ingestRiskAnalysisContent(state.doc, action.payload)
        })
        builder.addCase(loadDocumentProcess.fulfilled, (state, action) => {
            if (state.doc) {
                state.doc.activeProcess = action.payload
            }
        })
        builder.addCase(deleteFunction.fulfilled, (state, action) => {
            const doc = state.doc
            if (doc && doc.functions.loaded) {
                fnAdapter.removeOne(doc.functions.items, action.payload)

                const removedId = Number(action.payload)
                Object.keys(doc.raGroupFunctions).forEach(groupId => {
                    doc.raGroupFunctions[groupId] =
                        doc.raGroupFunctions[groupId].filter(id => id !== removedId)
                })
            }
        })
        builder.addMatcher(isAnyOf(updateComment.fulfilled, changeDocCommentStatus.fulfilled), (state, action) => {
            if (state.doc && state.doc.comments) {
                const { id, ...rest } = action.payload
                const updt = {
                    id,
                    changes: rest,
                }
                commentAdapter.updateOne(state.doc.comments, updt)
            }
        })
        builder.addMatcher(isAnyOf(
            evaluateDeviation.fulfilled,
            analyseDeviation.fulfilled,
            correctionDeviation.fulfilled,
            closeDeviation.fulfilled
        ), (state, action) => {
            if (state.doc && state.doc.deviations !== null) {
                const { id, tests,  ...changes } = action.payload
                deviationAdapter.updateOne(state.doc.deviations.items, {
                    id, changes
                })
            }
        })
    },
})

export const selectDocument = (state: RootState) => state.document

export const sectionSelectors = sectionAdapter.getSelectors<RootState>(s => s.document.doc?.sections ?? sectionAdapter.getInitialState())

export const blocksSelector = blockAdapter.getSelectors<RootState>(s => s.document.doc?.blocks ?? blockAdapter.getInitialState())

export const docWfActionsSelectors = docWfActionsAdapter.getSelectors<RootState>(s => s.document.doc?.workflowActions ?? docWfActionsAdapter.getInitialState())

export const docActionsSelectors = docActionsAdapter.getSelectors<RootState>(s => s.document.doc?.actions ?? docActionsAdapter.getInitialState())

export const docTestsSelectors = testAdapter.getSelectors<RootState>(s => s.document.doc?.tests.tests ?? testAdapter.getInitialState())
export const docTestsRunsSelectors = testRunAdapter.getSelectors<RootState>(s => s.document.doc?.tests.runs ?? testRunAdapter.getInitialState())
export const docTestsResultsSelectors = testResultAdapter.getSelectors<RootState>(s => s.document.doc?.tests.results ?? testResultAdapter.getInitialState())
export const docDeviationsSelectors = deviationAdapter.getSelectors<RootState>(s => s.document.doc?.deviations.items ?? deviationAdapter.getInitialState())
export const docFnSelectors = fnAdapter.getSelectors<RootState>(s => s.document.doc?.functions.items ?? fnAdapter.getInitialState())

/**
 * RA group tree assembled entirely from redux: the hierarchy comes from the
 * document sections (the RISK_ANALYSIS section and its `subSectionsIds`), and the
 * functions are resolved from the flat function entities via `raGroupFunctions`.
 */
export interface RaGroupNode {
    section: DocumentSection
    functions: RiskAnalysisFunction[]
    groups: RaGroupNode[]
}

export const selectRaGroupTree = createSelector(
    (state: RootState) => sectionSelectors.selectEntities(state),
    (state: RootState) => sectionSelectors.selectAll(state),
    (state: RootState) => docFnSelectors.selectEntities(state),
    (state: RootState) => state.document.doc?.raGroupFunctions ?? {},
    (sectionsById, allSections, functionsById, groupFunctions): RaGroupNode[] => {
        const raSection = allSections.find(s => s.type === 'RISK_ANALYSIS')
        if (!raSection) {
            return []
        }

        // Guards against a malformed/cyclic section graph rather than trusting it.
        const seen = new Set<AppId>()
        const build = (sectionId: AppId): RaGroupNode | null => {
            if (seen.has(sectionId)) {
                return null
            }
            seen.add(sectionId)

            const section = sectionsById[sectionId]
            if (!section) {
                return null
            }

            return {
                section,
                functions: (groupFunctions[sectionId] ?? [])
                    .map(id => functionsById[id])
                    .filter((fn): fn is RiskAnalysisFunction => Boolean(fn)),
                groups: (section.subSectionsIds ?? [])
                    .map(build)
                    .filter((n): n is RaGroupNode => n !== null),
            }
        }

        seen.add(raSection.id)
        return (raSection.subSectionsIds ?? [])
            .map(build)
            .filter((n): n is RaGroupNode => n !== null)
    }
)

/** function id -> the id of the group (section) holding it. */
export const selectFunctionGroupIds = createSelector(
    (state: RootState) => state.document.doc?.raGroupFunctions ?? {},
    (groupFunctions): Map<number, AppId> => {
        const byFunction = new Map<number, AppId>()
        Object.entries(groupFunctions).forEach(([groupId, ids]) =>
            ids.forEach(id => byFunction.set(id, groupId)))
        return byFunction
    }
)

/** Functions attached straight to the RISK_ANALYSIS section rather than to a group. */
export const selectRaUngroupedFunctions = createSelector(
    (state: RootState) => sectionSelectors.selectAll(state),
    (state: RootState) => docFnSelectors.selectEntities(state),
    (state: RootState) => state.document.doc?.raGroupFunctions ?? {},
    (allSections, functionsById, groupFunctions): RiskAnalysisFunction[] => {
        const raSection = allSections.find(s => s.type === 'RISK_ANALYSIS')
        if (!raSection) {
            return []
        }
        return (groupFunctions[raSection.id] ?? [])
            .map(id => functionsById[id])
            .filter((fn): fn is RiskAnalysisFunction => Boolean(fn))
    }
)

export const
    { swapSections
    , updateDocumentHeader
    , changeTestResult
    , changeTestRun
    , pushDefect
    , delDefect
    , pushConsequence
    , delConsequence
    , pushReason
    , delReason
    , setDefect
    , setConsequence
    , setReason
    , setFunctions
    , replaceConsequences
    , replaceReasons
    } = documentSlice.actions

export const commentsSelectors = commentAdapter.getSelectors<RootState>(s => s.document.doc?.comments ?? commentAdapter.getInitialState())

export const isDocEditable = createSelector(
    (state: RootState) => selectDocument(state).doc?.actions,
    actions => actions?.entities["Edit"] !== undefined
)

export const isDocCommentable = createSelector(
    (state: RootState) => selectDocument(state).doc?.actions,
    actions => Boolean(actions?.entities["Comment"])

)

export const isDocCommentResolvable = createSelector(
    (state: RootState) => selectDocument(state).doc?.actions,
    actions => Boolean(actions?.entities["ResolveComment"])

)

export const showDocComments = createSelector(
    (state: RootState) => selectDocument(state).doc,
    (state: RootState) => isDocCommentable(state),
    (state: RootState) => commentsSelectors.selectAll(state),
    (_: RootState, sectionId: string) => sectionId,
    (doc, isCommentable, comments, sectionId) => Boolean(isCommentable || (doc?.header.status !== 'N' && comments.filter(c => c.sectionGuid === sectionId).length > 0))
)

export default documentSlice.reducer
